import React from 'react';
import DocumentTitle from 'react-document-title';
import { FormattedMessage } from 'react-intl';

import {
  Table,
  TableHeader,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import { Switch } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { CheckIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import TableLayout from '../../components/TableLayout';
import useBaseTableProps from '../../hooks/useBaseTableProps';
import useSelectedProperty from '../../hooks/useSelectedProperty';
import useIntl from '../../hooks/useIntl';

import useManageAffordableWaitlistV2 from './hooks';
import ManageAffordableWaitlistV2Tabs from './ManageAffordableWaitlistV2Tabs';
import ManageAffordableWaitlistV2Buttons from './ManageAffordableWaitlistV2Buttons';
import SelectFromWaitlist from '../../components/AffordableWaitlistActions/SelectFromWaitlist';
import ConfigurePreferences from './ConfigurePreferences';
import DenyFromWaitlist from '../../components/AffordableWaitlistActions/DenyFromWaitlist';
import CancelFromWaitlist from '../../components/AffordableWaitlistActions/CancelFromWaitlist';
import ReinstateFromWaitlist from '../../components/AffordableWaitlistActions/ReinstateFromWaitlist';
import messages from './messages';

const ManageAffordableWaitlistV2 = () => {
  const intl = useIntl();
  const baseTableProps = useBaseTableProps();
  const { id: propertyId, organizationId } = useSelectedProperty();

  const {
    isLoading,
    count,
    totalCount,
    order,
    orderBy,
    handleSortChange,
    filterState,
    filterTypeState,
    searchState,
    dateState,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    allColumnsHidden,
    columnOptions,
    selectedColumns,
    handleColumnChange,
    filteredHeaders,
    rows,
    bedroomCounts,
    floorPlans,
    selectedBedroomCount,
    setSelectedBedroomCount,
    separateByBedroom,
    name,
    refetchPositions,
    selectFromWaitlistModalProps,
    denyModalProps,
    cancelModalProps,
    reinstateModalProps,
    waitlistV2Programs,
    onCSVButtonClick,
    onPDFButtonClick,
    affordableWaitlistEnabled,
    configurePreferencesModalProps,
    configurePreferencesDefaultValues,
    refetchAffordableWaitlistPropertyPreferences,
    isIncludeAll,
    setIsIncludeAll,
  } = useManageAffordableWaitlistV2({ organizationId, propertyId });

  return (
    <DocumentTitle
      data-testid={'manage-affordable-waitlist-v2'}
      title={intl.formatMessage(messages.title)}
    >
      <TableLayout name={name} containerElementsHeight={40 + 56 + 8}>
        {(tableHeight) => (
          <>
            <TableHeader
              title={<FormattedMessage {...messages.title} />}
              actions={
                <ManageAffordableWaitlistV2Buttons
                  organizationId={organizationId}
                  propertyId={propertyId}
                  bedroomCount={selectedBedroomCount}
                  disabled={isLoading || !affordableWaitlistEnabled}
                  openConfigurePreferences={() =>
                    configurePreferencesModalProps.handleOpen({})
                  }
                />
              }
              Tabs={
                separateByBedroom && bedroomCounts.length > 0 ? (
                  <ManageAffordableWaitlistV2Tabs
                    bedroomCounts={bedroomCounts}
                    selectedBedroomCount={selectedBedroomCount}
                    onChange={setSelectedBedroomCount}
                  />
                ) : undefined
              }
              count={count}
              totalCount={totalCount}
            />
            <Switch
              checked={isIncludeAll}
              onChange={() => setIsIncludeAll((prevState) => !prevState)}
              label={<FormattedMessage {...messages.includeAll} />}
              icon={<CheckIcon />}
              checkedIcon={<CheckIcon />}
              id="include-all"
              disabled={isLoading}
            />
            <Table
              {...baseTableProps}
              handleSortChange={handleSortChange}
              handleFilterChange={handleFilterChange}
              handleFilterTypeChange={handleFilterTypeChange}
              handleSearchSubmit={handleSearchSubmit}
              handleDateSubmit={handleDateSubmit}
              filterState={filterState}
              filterTypeState={filterTypeState}
              searchState={searchState}
              dateState={dateState}
              handleColumnChange={handleColumnChange}
              allColumnsHidden={allColumnsHidden}
              columnOptions={columnOptions}
              selectedColumns={selectedColumns}
              isLoading={isLoading}
              count={count}
              totalCount={totalCount}
              name={name}
              order={order}
              orderBy={orderBy}
              headers={filteredHeaders}
              rows={rows}
              onCSVButtonClick={count > 0 ? onCSVButtonClick : undefined}
              onPDFButtonClick={count > 0 ? onPDFButtonClick : undefined}
              height={tableHeight}
            />
            <SelectFromWaitlist
              organizationId={organizationId}
              propertyId={propertyId}
              open={selectFromWaitlistModalProps.open}
              handleClose={selectFromWaitlistModalProps.handleClose}
              modalState={selectFromWaitlistModalProps.modalState}
              refetchPositions={refetchPositions}
              separateByBedroom={separateByBedroom}
              waitlistV2Programs={waitlistV2Programs}
            />
            <DenyFromWaitlist
              organizationId={organizationId}
              propertyId={propertyId}
              affordableWaitlistId={
                denyModalProps.modalState.affordableWaitlistId
              }
              open={denyModalProps.open}
              handleClose={denyModalProps.handleClose}
              onSuccess={() => {
                denyModalProps.handleClose();
                refetchPositions();
              }}
            />
            <CancelFromWaitlist
              organizationId={organizationId}
              propertyId={propertyId}
              affordableWaitlistId={
                cancelModalProps.modalState.affordableWaitlistId
              }
              open={cancelModalProps.open}
              handleClose={cancelModalProps.handleClose}
              onSuccess={() => {
                cancelModalProps.handleClose();
                refetchPositions();
              }}
            />
            <ReinstateFromWaitlist
              organizationId={organizationId}
              propertyId={propertyId}
              affordableWaitlistId={
                reinstateModalProps.modalState.affordableWaitlistId
              }
              applicationId={reinstateModalProps.modalState.applicationId}
              floorPlans={floorPlans}
              open={reinstateModalProps.open}
              handleClose={reinstateModalProps.handleClose}
              onSuccess={() => {
                reinstateModalProps.handleClose();
                refetchPositions();
              }}
            />
            <ConfigurePreferences
              organizationId={organizationId}
              propertyId={propertyId}
              open={configurePreferencesModalProps.open}
              handleClose={configurePreferencesModalProps.handleClose}
              defaultValues={configurePreferencesDefaultValues}
              refetchPreferences={refetchAffordableWaitlistPropertyPreferences}
            />
          </>
        )}
      </TableLayout>
    </DocumentTitle>
  );
};

export default ManageAffordableWaitlistV2;
